/**
 * Class to implement the scroll-spy functionality
 * for a single instance; the application is done
 * via the ScrollSpyManager class
 */
export class ScrollSpy {
    private anchorContexts: Element[];
    private anchorMapping: Map<
        string,
        {
            target: Element;
            anchors: Set<HTMLAnchorElement>;
        }
    > = new Map();

    private current: string | null = null;

    /**
     * Constructor of the ScrollSpy
     *
     * @param {HTMLElement} scrollSpy
     */
    public constructor(private scrollSpy: HTMLElement) {
        const id = scrollSpy.getAttribute("scroll-spy");
        this.anchorContexts = Array.from(
            document.querySelectorAll("[scroll-spy-anchors]"),
        ).filter(
            (context) => context.getAttribute("scroll-spy-anchors") === id,
        );

        // Create the scroll-spy
        this.instantiate();
    }

    /**
     * Instantiates the functionality of the scroll-spy
     *
     * @returns {void}
     */
    private instantiate(): void {
        this.anchorContexts?.forEach((anchorContext) => {
            const anchors =
                anchorContext.querySelectorAll<HTMLAnchorElement>(
                    `a[href^="#"]`,
                );
            anchors?.forEach((anchor) => {
                const id = anchor.getAttribute("href")?.slice(1);
                if (id) {
                    if (this.anchorMapping.has(id)) {
                        this.anchorMapping.get(id)!.anchors.add(anchor);
                    } else {
                        const target = Array.from(
                            this.scrollSpy.querySelectorAll("[id]"),
                        ).find((element) => element.id === id);
                        if (target) {
                            this.anchorMapping.set(id, {
                                target: target,
                                anchors: new Set([anchor]),
                            });
                        }
                    }
                }
            });
        });

        // Listen to scroll
        if (this.anchorMapping.size === 0) return;
        window.addEventListener("scroll", this.evaluate, {
            passive: true,
        });

        // Initial evaluation
        this.evaluate();
    }

    /**
     * Method to handle the scroll-event
     *
     * @returns {void}
     */
    private evaluate = (): void => {
        let current: string | null = null;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        for (const [id, { target }] of this.anchorMapping) {
            const rect = target.getBoundingClientRect();

            const targetTop = rect.top + scrollTop;
            if (targetTop <= scrollTop + this.threshold) {
                current = id;
            }
        }

        this.setCurrent(current);
    };

    /**
     * Method to set the currently active target
     *
     * @param {string} id
     * @returns {void}
     */
    private setCurrent(id: string | null): void {
        if (this.current === id) return;
        this.current = id;

        this.anchorMapping.forEach((mapping, anchorId) => {
            if (anchorId === id) {
                mapping.anchors.forEach((anchor) => {
                    anchor.setAttribute("aria-current", "true");
                });
            } else {
                mapping.anchors.forEach((anchor) => {
                    anchor.removeAttribute("aria-current");
                });
            }
        });
    }

    public destroy(): void {
        window.removeEventListener("scroll", this.evaluate);
        this.anchorMapping.forEach(({ anchors }) => {
            anchors.forEach((anchor) => anchor.removeAttribute("aria-current"));
        });
        this.anchorMapping.clear();
        this.current = null;
    }

    /**
     * Method to read and calculate the offset
     * from predefined CSS variables
     *
     * @returns {number}
     */
    private get threshold(): number {
        const headerCompensation = parseInt(
            getComputedStyle(document.body)
                .getPropertyValue("--header-height")
                .trim()
                .replace("px", ""),
        );

        const stickyTopOffset = parseInt(
            getComputedStyle(document.body)
                .getPropertyValue("--sticky-top-offset")
                .trim()
                .replace("px", ""),
        );

        if (isNaN(headerCompensation)) return 150;
        if (isNaN(stickyTopOffset)) return headerCompensation + 32;
        return headerCompensation + stickyTopOffset;
    }
}
